import React, { useEffect, useState } from "react";
import "../components/InvoiceList.css";
import { FaFileInvoice, FaCheckCircle, FaEdit, FaPrint, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { hasPermission } from "../utils/permissions";

const API_BASE = "https://newerp.awasthivitality.com/api";

const InvoiceList = () => {
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  // dont remove comment it -> invoice permission states for edit/delete buttons
  const [canEditInvoice, setCanEditInvoice] = useState(false);
  const [canDeleteInvoice, setCanDeleteInvoice] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const invoicesPerPage = 10;
const deleteInvoice = async (invoiceId) => {
  const confirmDelete = window.confirm(
    "Are you sure you want to delete this invoice?"
  );
  if (!confirmDelete) return;

  try {
    const token = (localStorage.getItem("session_token") || "").trim();

    const response = await fetch(
      `${API_BASE}/invoices/${invoiceId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(token ? { "X-Session-Token": token } : {}),
        },
      }
    );

    const res = await response.json().catch(() => ({}));

    if (response.ok && res.success) {
      toast.success(" Invoice deleted successfully");
      // Remove invoice instantly from UI
      setInvoices((prev) =>
        prev.filter((inv) => inv.invoice_id !== invoiceId)
      );
    } else {
      toast.error(res?.message || " Failed to delete invoice");
    }
  } catch (error) {
    console.error("Delete invoice error:", error);
    toast.error("❌ Network error. Please try again.");
  }
};

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const token = (localStorage.getItem("session_token") || "").trim();

      const url = new URL(`${API_BASE}/invoices/list`);
      if (token) url.searchParams.set("token", token);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(token ? { "X-Session-Token": token } : {}),
        },
      });

      const res = await response.json().catch(() => ({}));

      if (response.ok && res && Array.isArray(res.data)) {
        setInvoices(res.data);
      } else {
        setInvoices([]);
        toast.error(res?.message || "Failed to load invoices");
      }
    } catch (error) {
      console.error("Invoice fetch error:", error);
      toast.error("❌ Network Error. Please try again.");
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();

    // dont remove comment it -> load invoice permissions on mount
    const loadPerms = async () => {
      try {
        const editPerm = await hasPermission("invoice", "invoiceeditview");
        const deletePerm = await hasPermission("invoice", "invoicedeleteview");
        setCanEditInvoice(!!editPerm);
        setCanDeleteInvoice(!!deletePerm);
      } catch (err) {
        console.error("Error loading invoice permissions:", err);
        setCanEditInvoice(false);
        setCanDeleteInvoice(false);
      }
    };
    loadPerms();
  }, []);

  // Pagination logic
  const indexOfLastInvoice = currentPage * invoicesPerPage;
  const indexOfFirstInvoice = indexOfLastInvoice - invoicesPerPage;
  const currentInvoices = invoices.slice(indexOfFirstInvoice, indexOfLastInvoice);
  const totalPages = Math.ceil(invoices.length / invoicesPerPage);

  if (loading) return <div>Loading invoices...</div>;

  return (
    <div className="invoice-container">
      <div className="header-bar">
        <h2>Invoices</h2>
        <button
          className="btn-generate"
          onClick={() => navigate("/generateinvoice")}
        >
          + Generate Invoice
        </button>
      </div>

      <div className="invoice-list">
        <div className="invoice-list-header">
          <span>Invoice No</span>
          <span>Date</span>
          <span>Patient</span>
          <span>Total</span>
          <span>Created By</span>
          <span>Status</span>
          <span>Actions</span>
        </div>

        {currentInvoices.map((inv) => (
          <div className="invoice-list-item" key={inv.invoice_id}>
            <span className="invoice-no">
              <FaFileInvoice /> {inv.invoice_no}
            </span>
            <span>{inv.date}</span>
            <span>{inv.patient_display}</span>
            <span>₹{inv.grand_total}</span>
            <span>{inv.created_by || "N/A"}</span>
            <span className={`status ${inv.pay_status?.toLowerCase()}`}>
              <FaCheckCircle /> {inv.pay_status}
            </span>
            <span className="invoice-actions">
              {canEditInvoice && (
                <button
                  className="btn-icon edit"
                  onClick={() => navigate(`/EditInvoice/${inv.invoice_id}`)}
                  title="Edit Invoice"
                >
                  <FaEdit />
                </button>
              )}
              <button
                className="btn-icon print"
                onClick={() => navigate(`/InvoicePrint/${inv.invoice_id}`)}
                title="View/Print Invoice"
              >
                <FaPrint />
              </button>
              {canDeleteInvoice && (
                <button
                  className="btn-icon delete"
                  onClick={() => deleteInvoice(inv.invoice_id)}
                  title="Delete Invoice"
                >
                  <FaTrash />
                </button>
              )}

            </span>
          </div>
        ))}
      </div>

      <div className="pagination">
        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
          Prev
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
          Next
        </button>
      </div>
    </div>
  );
};

export default InvoiceList;
